import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'Understand Paddiq session billing and free uploads | Paddiq';

export function SessionBillingPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Billing guide</p>
            <h1>Understand session billing.</h1>
            <p className="guide-hero__lead">
              See what is free, what an active month costs, whose account pays for an upload, and
              how to keep session uploads available.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/settings?section=billing')}
              >
                Review usage billing
              </a>
              <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/#pricing">
                See pricing
              </a>
            </div>
            <p className="guide-hero__meta">Web and iPhone guide · Reviewed October 16, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/session-billing" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#pricing">Know what an upload costs</a>
                </li>
                <li>
                  <a href="#organization-invoicing">Request organization invoicing</a>
                </li>
                <li>
                  <a href="#partner-access">Use active partner access</a>
                </li>
                <li>
                  <a href="#payer">Confirm who is billed</a>
                </li>
                <li>
                  <a href="#review">Review usage and payment</a>
                </li>
                <li>
                  <a href="#paused">Restore paused uploads</a>
                </li>
                <li>
                  <a href="#questions">Common questions</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>At a glance</strong>
                <span>
                  Your first five session uploads are free. No billable session uploads means no
                  session usage charge.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="pricing">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Session pricing</p>
                  <h2>Start with five free uploads.</h2>
                  <ol className="guide-instructions">
                    <li>Your first five session uploads ever are free.</li>
                    <li>You do not need a payment method for those five uploads.</li>
                    <li>
                      After the free allowance, a monthly billing period with billable uploads costs
                      $9 and includes the first five billable sessions in that period.
                    </li>
                    <li>Each additional billable session in that period costs $1.</li>
                  </ol>
                  <div className="guide-callout">
                    Under usage billing, a period with no billable session uploads has no session
                    usage charge. The period follows the account’s billing cycle, which may not
                    match the calendar month. GoPro processing and storage are tracked separately
                    and are not currently billed. Existing sessions remain available for review even
                    when new uploads are paused.
                  </div>
                </div>
              </section>

              <section
                className="guide-callout"
                id="organization-invoicing"
                aria-labelledby="organization-invoicing-title"
                style={{ marginBottom: 32 }}
              >
                <h2 id="organization-invoicing-title">Fixed monthly invoicing for organizations</h2>
                <p>
                  Contact Paddiq to discuss an agreed monthly price and invoice billing for your
                  organization. Pricing and setup are handled with our team.
                </p>
                <p>
                  New teams start on usage billing. Fixed monthly invoicing starts only after an
                  external agreement and setup by a Paddiq administrator.
                </p>
                <p>
                  Ask us to confirm availability, billing terms, and your start date. Sending a
                  request does not change your current plan.
                </p>
                <a
                  className="landing-btn landing-btn--primary"
                  href="mailto:support@paddiq.app?subject=Organization%20invoice%20billing"
                >
                  Request invoicing
                </a>
              </section>

              <section className="guide-step" id="partner-access">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Partner access</p>
                  <h2>Keep uploading while partner access is active.</h2>
                  <ol className="guide-instructions">
                    <li>
                      An active Paddiq partner code lets the team continue uploading after the first
                      five free sessions without adding a payment method.
                    </li>
                    <li>
                      On the web, the team owner can open <strong>Usage billing</strong>, enter the
                      code under <strong>Partner code</strong>, and choose{' '}
                      <strong>Apply code</strong>.
                    </li>
                    <li>
                      Wait for <strong>Partner access applied</strong>, then retry the upload that
                      was paused.
                    </li>
                    <li>
                      When partner access ends, the regular payment rules apply to later uploads.
                    </li>
                  </ol>
                  <div className="guide-callout">
                    While partner access is active, the panel is titled{' '}
                    <strong>Account access</strong> and shows{' '}
                    <strong>Complimentary access is active</strong>. Session uploads cost $0 during
                    that access. If billing was already set up,{' '}
                    <strong>Manage existing billing</strong> remains available.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="payer">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Billing account</p>
                  <h2>Check which account owns the usage.</h2>
                  <ol className="guide-instructions">
                    <li>
                      In the updated billing experience, the driver's current team is responsible
                      for the upload, including uploads made by an authorized parent.
                    </li>
                    <li>
                      Without a team, the verified parent or responsible personal account pays. Ask
                      support if the responsible account cannot be determined.
                    </li>
                    <li>Sharing an uploaded session with a team does not add a charge.</li>
                    <li>
                      If a team payment needs attention, ask the team owner to open{' '}
                      <strong>Billing</strong> for the relevant account.
                    </li>
                  </ol>
                </div>
              </section>

              <aside
                className="guide-callout"
                aria-label="Billing panel names"
                style={{ marginBottom: 32 }}
              >
                <strong>Which panel will you see?</strong>
                <p>
                  <strong>Usage billing</strong> shows your free uploads, session usage, and
                  estimate when standard billing applies.
                </p>
                <p>
                  <strong>Account access</strong> shows your partner coverage when complimentary
                  access is active. Session usage costs $0 while that coverage lasts.
                </p>
              </aside>

              <section className="guide-step" id="review">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Usage and payment</p>
                  <h2>Open Billing before the next upload.</h2>
                  <ol className="guide-instructions">
                    <li>
                      In the updated web experience, open <strong>Billing</strong> from the sidebar.
                      Older web versions keep billing under <strong>Settings</strong>. Existing
                      billing links redirect to the full page when it is available.
                    </li>
                    <li>
                      On iPhone, open <strong>App Settings</strong>, choose{' '}
                      <strong>User Settings</strong>, then find <strong>Usage billing</strong> or{' '}
                      <strong>Account access</strong> when partner coverage is active.
                    </li>
                    <li>
                      For standard session billing, review <strong>First 5 sessions ever</strong>,{' '}
                      <strong>Sessions this month</strong>, and{' '}
                      <strong>Estimated this month</strong>.
                    </li>
                    <li>
                      On the web, choose <strong>Add payment method</strong> when a card is
                      required, or <strong>Manage payment method</strong> when one is already
                      attached.
                    </li>
                  </ol>
                  <p>
                    Billing controls appear only for the person who can manage that billing account.
                    Other team members can ask the team owner for help.
                  </p>
                  <p>
                    iPhone controls depend on the installed app version. Newer status-and-retry
                    screens leave payment management on the web; use Billing there, then return to
                    the saved upload and check access again. A web update does not establish when
                    that native screen became available.
                  </p>
                  <p>
                    For an activated fixed monthly agreement, Billing shows the agreed amount,
                    invoices, payment status, and available payment actions. Automatic payments
                    require explicit authorization. Turning them off changes future collection to
                    emailed invoices; it does not cancel the agreement or change the amount, renewal
                    date, saved card, or existing invoices.
                  </p>
                  <p>
                    On the web, the billing administrator can choose{' '}
                    <strong>Deactivate at period end</strong> to stop an active usage subscription.
                    It ends at the close of the current billing period; active partner coverage
                    still allows covered uploads. Once the subscription has ended,{' '}
                    <strong>Remove saved card</strong> is available when a card remains. Removing
                    saved cards does not restore paid upload access.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="paused">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Upload access</p>
                  <h2>Resolve the payment prompt, then try the upload again.</h2>
                  <ol className="guide-instructions">
                    <li>Open the billing prompt or Billing for the relevant account.</li>
                    <li>
                      Add or update the payment method, or pay the outstanding invoice when offered.
                      Updating a card alone does not settle an overdue invoice.
                    </li>
                    <li>
                      Return to Paddiq. On iPhone, choose <strong>Check access again</strong> if
                      that action appears.
                    </li>
                    <li>Retry the saved upload after Paddiq confirms access.</li>
                  </ol>
                  <div className="guide-callout">
                    A downloaded iPhone session and its assignment choices stay saved when billing
                    pauses the cloud upload.
                  </div>
                </div>
              </section>

              <section className="guide-troubleshooting" id="questions">
                <p className="guide-step__eyebrow">Common questions</p>
                <h2>How session billing behaves</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>Do I pay in a month when I upload nothing?</h3>
                    <p>
                      Under usage billing, no session usage charge applies when there are no
                      billable session uploads in the billing period. GoPro processing and storage
                      are tracked separately and are not currently billed. An activated fixed
                      monthly agreement follows its agreed monthly price even when no sessions are
                      uploaded.
                    </p>
                  </article>
                  <article>
                    <h3>Can I still open earlier sessions?</h3>
                    <p>
                      Yes. A payment prompt pauses new uploads; sessions already in Paddiq remain
                      available for review.
                    </p>
                  </article>
                  <article>
                    <h3>Does sharing a session charge the team again?</h3>
                    <p>No. Sharing a session with the team is free.</p>
                  </article>
                  <article>
                    <h3>Why can’t I see payment controls?</h3>
                    <p>
                      You may not manage the billing account shown. Ask the team owner, or contact
                      support if the responsible account looks wrong.
                    </p>
                  </article>
                  <article>
                    <h3>Why is an upload blocked when my team has partner access?</h3>
                    <p>
                      Confirm that the partner code is still active and belongs to the team shown
                      under <strong>Account</strong> in Account access. Contact support if the
                      upload remains paused.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Ready for the next session?</p>
                  <h2>Check the account and uploads remaining.</h2>
                  <p>
                    Open Billing before track time so the person responsible for payment can resolve
                    any prompt.
                  </p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/settings?section=billing')}
                  >
                    Review usage billing
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
